﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

import * as React from "react";
import { Button } from "~base/Components/Atoms/Button";
import Form from "~base/Components/Primitives/Form";
import TitleSelect from "~base/Components/Primitives/TitleSelect";
import { localeSelectValues } from "~base/Pages/Shared/LocalePicker";
import { useIntl } from "~base/Internationalization/IntlContext";
import { getErrorMessage } from "~base/Services/API";
import { useServerContext } from "~base/Services/ServerContext";
import { useSettings } from "~base/Settings/SettingsContext";
import {
    buildReducerId,
    useConsilioReducer,
} from "~base/Utilities/ConsilioReducer";
import { usePutUserSettings } from "~jrad/Services/API/Account/PutUserSettings";
import TextBlock from "~base/Components/Primitives/TextBlock";
import ApplicationPage from "~base/Pages/Shared/ApplicationPage";
import CustomerHeaderMenu from "~jrad/Pages/Shared/CustomerHeaderMenu";
import { reducer } from "./Store";
import { Rubrik } from "~base/Components/Primitives/Rubrik";
import { useStyles } from "./Styles";
import { useNavigateService } from "~base/Services/NavigateService";

// ----------------------------------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------------------------------

interface CustomerSettingsPageProps {}

// ----------------------------------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------------------------------

export const CustomerSettingsPage: React.FunctionComponent<
    CustomerSettingsPageProps
> = () => {
    const classes = useStyles();
    const intl = useIntl();
    const serverContext = useServerContext();
    const settings = useSettings();
    const navigate = useNavigateService();

    const [state, dispatch, _] = useConsilioReducer(
        buildReducerId("CustomerSettingsPage"),
        reducer,
        {
            uiCulture:
                serverContext.user?.PreferredUiCulture ??
                settings.valueAsString("account.defaultLanguage"),
            hasEditedForm: false,
        }
    );

    React.useEffect(() => {
        dispatch({
            type: "setField",
            field: "uiCulture",
            markAsChanged: false,
            value:
                serverContext.user?.PreferredUiCulture ??
                settings.valueAsString("account.defaultLanguage"),
        });
    }, [dispatch, serverContext.user, settings]);

    // Change URL if a navigation has been dispatched.
    React.useEffect(() => {
        if (state.navigateUri) {
            navigate.current(
                state.navigateUri,
                { replace: state.navigateReplace },
                state.navigateForceLoad
            );
        }
    }, [
        navigate,
        state.navigateUri,
        state.navigateReplace,
        state.navigateForceLoad,
    ]);

    const [updatingUser, updateUserRequest] = usePutUserSettings({
        callback: async (
            status,
            response,
            responseData,
            requestData,
            context,
            traceContext
        ) => {
            if (status == "Failed") {
                dispatch({
                    type: "setErrorMessage",
                    message: await getErrorMessage(intl, response),
                });
            } else {
                dispatch({
                    type: "navigate",
                    uri: "/Home/Settings",
                    forceLoad: true,
                });
            }
        },
    });

    const languages = localeSelectValues(
        settings
            .valueAsStringArray("cultures")
            .map((lang) => (lang === "en-NL" ? "en-US" : lang))
    );

    const pageLoading =
        state.loading || !serverContext.user || !!state.navigateUri;

    const showPageHeader = !pageLoading || !!state.navigateUri;

    return (
        <ApplicationPage
            showHeader={showPageHeader}
            isLoading={pageLoading}
            headerMenu={<CustomerHeaderMenu />}
            centerVertical={false}
            handleMargins={false}
            alignText="Left"
            contentWrapperClassName={classes.page}
            showFooter={false}
        >
            <Rubrik
                rubrikClassName={classes.pageHeader}
                textCode="SoPNL.UI.CustomerSettingsPage.Header"
            />
            <Form
                testIdentifier="CustomerSettings"
                className={classes.form}
                onSubmit={() => {
                    updateUserRequest({
                        PreferredUiCulture: state.uiCulture,
                    });
                }}
            >
                {state.errorMessage && (
                    <TextBlock
                        styleType="Paragraph"
                        text={state.errorMessage}
                        styleSubType="Validation"
                        size="Small"
                    />
                )}
                <TitleSelect
                    testIdentifier="SoPNL.UI.CustomerStartPage.Settings.Language"
                    required
                    enabled={!updatingUser}
                    titleTextCode="SoPNL.UI.CustomerStartPage.Settings.Language"
                    value={state.uiCulture}
                    stringValues={languages}
                    onChange={(value) => {
                        dispatch({
                            type: "setField",
                            field: "uiCulture",
                            markAsChanged: true,
                            value,
                        });
                    }}
                />
                <Button
                    testIdentifier="SaveCustomerSettings"
                    type="submit"
                    disabled={!(state.hasEditedForm && !updatingUser)}
                    className={classes.submitButton}
                >
                    {intl.formatText(
                        "SoPNL.UI.CustomerStartPage.Settings.Save"
                    )}
                </Button>
            </Form>
        </ApplicationPage>
    );
};
